import React, { useState } from 'react';
import { AlertCircleIcon, ArrowLeftIcon, CheckCircleIcon, XIcon } from 'lucide-react';
interface BulkPaymentPreviewProps {
  data: any[];
  onBack: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}
export function BulkPaymentPreview({
  data,
  onBack,
  onConfirm,
  isSubmitting
}: BulkPaymentPreviewProps) {
  const [selectedRows, setSelectedRows] = useState<number[]>([...Array(data.length).keys()]);
  const toggleRow = (index: number) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter(i => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };
  const toggleAllRows = () => {
    if (selectedRows.length === data.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...Array(data.length).keys()]);
    }
  };
  const hasErrors = data.some(row => {
    return !row.customerName || !row.email || !row.amount || !row.description || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0;
  });
  // Calculate totals
  const selectedData = data.filter((_, index) => selectedRows.includes(index));
  const totalAmount = selectedData.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const currency = data[0]?.currency || 'MYR';
  return <div className="space-y-6" data-id="element-1267">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1268">
        <div className="flex justify-between items-center mb-6" data-id="element-1269">
          <h3 className="text-lg font-medium text-gray-800" data-id="element-1270">Preview Batch</h3>
          <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-800" data-id="element-1271">
            <ArrowLeftIcon size={16} className="mr-1" data-id="element-1272" />
            Back to Upload
          </button>
        </div>
        {hasErrors && <div className="p-4 bg-red-50 text-red-700 rounded-md flex items-start mb-6" data-id="element-1273">
            <AlertCircleIcon size={18} className="mt-0.5 mr-3 flex-shrink-0" data-id="element-1274" />
            <div data-id="element-1275">
              <p className="text-sm font-medium" data-id="element-1276">Some rows have errors</p>
              <p className="text-sm mt-1" data-id="element-1277">
                Please fix the issues highlighted in red before proceeding. All
                required fields must be completed, and payment amounts must be
                valid numbers greater than zero.
              </p>
            </div>
          </div>}
        <div className="border border-gray-200 rounded-md overflow-hidden mb-6" data-id="element-1278">
          <div className="max-h-[400px] overflow-auto" data-id="element-1279">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1280">
              <thead className="bg-gray-50 sticky top-0" data-id="element-1281">
                <tr data-id="element-1282">
                  <th className="px-4 py-3 text-left" data-id="element-1283">
                    <div className="flex items-center" data-id="element-1284">
                      <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked={selectedRows.length === data.length} onChange={toggleAllRows} data-id="element-1285" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1286">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1287">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1288">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1289">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1290">
                    Reference
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1291">
                {data.map((row, index) => {
                const hasError = !row.customerName || !row.email || !row.amount || !row.description || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0;
                return <tr key={index} className={hasError ? 'bg-red-50' : ''} data-id="element-1292">
                      <td className="px-4 py-3" data-id="element-1293">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked={selectedRows.includes(index)} onChange={() => toggleRow(index)} disabled={hasError} data-id="element-1294" />
                      </td>
                      <td className={`px-4 py-3 text-sm ${!row.customerName ? 'text-red-600' : 'text-gray-900'}`} data-id="element-1295">
                        {row.customerName || 'Missing name'}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-1296">
                        <div className={!row.email ? 'text-red-600' : 'text-gray-900'} data-id="element-1297">
                          {row.email || 'Missing email'}
                        </div>
                        {row.phone && <div className="text-gray-500 text-xs mt-1" data-id="element-1298">
                            {row.phone}
                          </div>}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium ${!row.amount || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-1299">
                        {!row.amount || isNaN(parseFloat(row.amount)) ? 'Invalid amount' : parseFloat(row.amount) <= 0 ? 'Amount must be > 0' : `${row.currency || currency} ${parseFloat(row.amount).toFixed(2)}`}
                      </td>
                      <td className={`px-4 py-3 text-sm ${!row.description ? 'text-red-600' : 'text-gray-500'}`} data-id="element-1300">
                        {row.description || 'Missing description'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1301">
                        {row.reference || '-'}
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex justify-between items-center" data-id="element-1302">
          <div data-id="element-1303">
            <p className="text-sm text-gray-600" data-id="element-1304">
              <span className="font-medium" data-id="element-1305">{selectedRows.length}</span> of{' '}
              <span className="font-medium" data-id="element-1306">{data.length}</span> payment
              requests selected
            </p>
            <p className="text-sm font-medium text-gray-800 mt-1" data-id="element-1307">
              Total: {currency} {totalAmount.toFixed(2)}
            </p>
          </div>
          <div className="flex space-x-4" data-id="element-1308">
            <button type="button" onClick={onBack} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors" data-id="element-1309">
              Back
            </button>
            <button type="button" onClick={onConfirm} disabled={selectedRows.length === 0 || hasErrors || isSubmitting} className={`
                px-4 py-2 font-medium rounded-md transition-colors
                ${selectedRows.length === 0 || hasErrors || isSubmitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}
              `} data-id="element-1310">
              {isSubmitting ? 'Creating Batch...' : 'Create Payment Requests'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}